import { HTMLAttributes, PropsWithChildren, useCallback } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { WCL_GRID_PADDING_SM } from '@/libraries/design/foundations/grid/src/base';
import { useTheme } from '@/libraries/design/utils/dark-theme-provider';

import { useScrollableTabList } from './use-scrollable-tab-list';

export type TabListProps = PropsWithChildren<{
	activeTabId?: string;
}> &
	HTMLAttributes<HTMLUListElement>;

const tabListStyles = css({
	position: 'relative',
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	overflowX: 'scroll',
	scrollSnapType: 'x mandatory',
	listStyle: 'none',
	margin: 0,
	padding: 0,
	['@supports (scrollbar-width: none)']: {
		scrollbarWidth: 'none',
	},
	[media.below.sm]: {
		gap: token('space.100'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'li:first-of-type': {
			paddingInlineStart: `${WCL_GRID_PADDING_SM}px`,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'li:last-of-type': {
			paddingInlineEnd: `${WCL_GRID_PADDING_SM}px`,
		},
	},
});

const highlightStyles = css({
	position: 'absolute',
	insetBlockStart: 0,
	height: '100%',
	borderRadius: token('space.400'),
	background: wacToken('color.primary.black'),
	[media.below.sm]: {
		display: 'none',
	},
	transition: 'left 0.3s ease-in-out, width 0.3s ease-in-out',
	pointerEvents: 'none',
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'none',
	},
});

const darkThemeHighlightStyles = css({
	background: wacToken('color.neutral.light0'),
});

const tabListContainerStyles = css({
	display: 'flex',
	alignItems: 'center',
	background: wacToken('color.neutral.light200'),
	[media.below.sm]: {
		background: 'transparent',
	},
	borderRadius: token('space.500'),
	padding: token('space.100'),
	width: 'fit-content',
	maxWidth: '100%',
});

const darkThemeTabListContainerStyles = css({
	background: wacToken('color.neutral.dark200'),
	[media.above.sm]: {
		border: `1px solid ${wacToken('color.neutral.dark600')}`,
	},
});
const awf1TabListContainerStyles = css({
	textTransform: 'uppercase',
	fontFamily: token('font.family.code'),
});

const scrollAreaStyles = css({
	position: 'relative',
	overflow: 'hidden',
	flex: 1,
	minWidth: 0,
});

const navButtonStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	background: 'none',
	border: 'none',
	cursor: 'pointer',
	padding: `0 ${token('space.200')}`,
	minWidth: token('space.300'),
	minHeight: token('space.300'),
	color: wacToken('color.primary.black'),
	flexShrink: 0,
	opacity: 1,
	transition: 'opacity 0.2s ease',
	[media.below.sm]: {
		display: 'none',
	},
});

const darkThemeNavButtonStyles = css({
	color: wacToken('color.neutral.light0'),
});

const hiddenNavButtonStyles = css({
	opacity: 0,
	pointerEvents: 'none',
	width: 0,
	minWidth: 0,
	padding: 0,
	overflow: 'hidden',
});

const fadeBaseStyles = css({
	position: 'absolute',
	insetBlockStart: 0,
	insetBlockEnd: 0,
	width: token('space.300'),
	pointerEvents: 'none',
	zIndex: 2,
	transition: 'opacity 0.2s ease',
});

const fadeStartStyles = css({
	insetInlineStart: 0,
	background: `linear-gradient(to right, ${wacToken('color.neutral.light200')}, transparent)`,
});

const fadeEndStyles = css({
	insetInlineEnd: 0,
	background: `linear-gradient(to left, ${wacToken('color.neutral.light200')}, transparent)`,
});

const darkThemeFadeStartStyles = css({
	background: `linear-gradient(to right, ${wacToken('color.neutral.dark200')}, transparent)`,
});

const darkThemeFadeEndStyles = css({
	background: `linear-gradient(to left, ${wacToken('color.neutral.dark200')}, transparent)`,
});

const hiddenFadeStyles = css({
	opacity: 0,
});

export const TabList = ({ children, activeTabId }: TabListProps) => {
	const intl = useIntl();

	const { isDarkTheme, isAwf1Theme } = useTheme();

	const { scrollableRef, highlightRef, isPreviousInactive, isNextInactive, handleScroll } =
		useScrollableTabList(activeTabId);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleScrollLeft = useCallback(() => {
		handleScroll(-1);
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'scrollTabsLeft',
			source: 'tabScrollControl',
			attributes: { label: 'scroll tabs left' },
		}).fire();
	}, [handleScroll, createAnalyticsEvent]);

	const handleScrollRight = useCallback(() => {
		handleScroll(1);
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'scrollTabsRight',
			source: 'tabScrollControl',
			attributes: { label: 'scroll tabs right' },
		}).fire();
	}, [handleScroll, createAnalyticsEvent]);

	return (
		<div
			css={[
				tabListContainerStyles,
				isDarkTheme && darkThemeTabListContainerStyles,
				isAwf1Theme && awf1TabListContainerStyles,
			]}
		>
			<button
				type="button"
				css={[
					navButtonStyles,
					isDarkTheme && darkThemeNavButtonStyles,
					isPreviousInactive && hiddenNavButtonStyles,
				]}
				onClick={handleScrollLeft}
				aria-label={intl.formatMessage({
					id: 'tab-list.scroll-left',
					defaultMessage: 'Scroll tabs left',
				})}
				tabIndex={isPreviousInactive ? -1 : 0}
				aria-hidden={isPreviousInactive}
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="7"
					height="12"
					viewBox="0 0 7 12"
					fill="none"
				>
					<path
						d="M5.8335 10.8333L0.833496 5.83334L5.8335 0.833344"
						stroke={
							isDarkTheme ? wacToken('color.neutral.light0') : wacToken('color.primary.black')
						}
						strokeWidth="1.66667"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			</button>
			<div css={scrollAreaStyles}>
				<div
					css={[
						fadeBaseStyles,
						fadeStartStyles,
						isDarkTheme && darkThemeFadeStartStyles,
						isPreviousInactive && hiddenFadeStyles,
					]}
					aria-hidden="true"
				/>
				<ul ref={scrollableRef} css={tabListStyles} role="tablist" aria-orientation="horizontal">
					<div
						ref={highlightRef}
						css={[highlightStyles, isDarkTheme && darkThemeHighlightStyles]}
						aria-hidden="true"
					/>
					{children}
				</ul>
				<div
					css={[
						fadeBaseStyles,
						fadeEndStyles,
						isDarkTheme && darkThemeFadeEndStyles,
						isNextInactive && hiddenFadeStyles,
					]}
					aria-hidden="true"
				/>
			</div>
			<button
				type="button"
				css={[
					navButtonStyles,
					isDarkTheme && darkThemeNavButtonStyles,
					isNextInactive && hiddenNavButtonStyles,
				]}
				onClick={handleScrollRight}
				aria-label={intl.formatMessage({
					id: 'tab-list.scroll-right',
					defaultMessage: 'Scroll tabs right',
				})}
				tabIndex={isNextInactive ? -1 : 0}
				aria-hidden={isNextInactive}
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="7"
					height="12"
					viewBox="0 0 7 12"
					fill="none"
				>
					<path
						d="M0.833496 10.8333L5.8335 5.83334L0.833496 0.833344"
						stroke={
							isDarkTheme ? wacToken('color.neutral.light0') : wacToken('color.primary.black')
						}
						strokeWidth="1.66667"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			</button>
		</div>
	);
};
